import { Controller } from '@hotwired/stimulus';

const URL_DELAY_MS = 500;
const SAVED_HIDE_MS = 2000;

const STATUS_BADGE_CLASSES = {
  captured: 'apps-script-harvest__badge apps-script-harvest__badge--captured',
  ready: 'apps-script-harvest__badge apps-script-harvest__badge--ready',
  failed: 'apps-script-harvest__badge apps-script-harvest__badge--failed',
  no_script: 'apps-script-harvest__badge apps-script-harvest__badge--no_script',
  pending: 'apps-script-harvest__badge apps-script-harvest__badge--pending'
};

const ROW_REMOVE_MS = 280;

export default class extends Controller {
  static targets = ['form', 'saveStatus', 'statusBadge', 'scriptId', 'errorMessage', 'noScriptButton', 'undoNoScriptButton', 'fetchButton', 'accountInput', 'accountOption'];

  connect() {
    this.timer = null;
    this.fadeTimer = null;
    this.inFlight = null;
    this.noScriptLock = false;
    this.removing = false;
    this.updateAccountSelection();
    this.snapshot = this.serialize();
  }

  disconnect() {
    this.clearTimer();
    this.clearFadeTimer();
  }

  scheduleUrlSave() {
    if (this.shouldDeferUrlOnlySave()) return;

    this.clearTimer();
    this.timer = window.setTimeout(() => this.save(), URL_DELAY_MS);
  }

  saveUrlNow() {
    if (this.noScriptLock) return;
    this.clearTimer();
    this.save();
  }

  saveAccount() {
    this.clearTimer();
    this.save();
  }

  selectAccount(event) {
    const value = event.currentTarget.dataset.accountValue;
    const input = this.accountInputTarget;
    if (!input || input.value === value) return;

    input.value = value;
    this.updateAccountSelection();
    this.saveAccount();
  }

  updateAccountSelection() {
    if (!this.hasAccountOptionTarget) return;

    const value = this.fieldValue('video[google_sheet_account]');
    this.accountOptionTargets.forEach((button) => {
      const selected = button.dataset.accountValue === value;
      button.classList.toggle('apps-script-harvest__account-option--selected', selected);
      button.setAttribute('aria-pressed', selected);
    });
  }

  updateNoScriptActions(status) {
    if (this.hasNoScriptButtonTarget) {
      this.noScriptButtonTarget.hidden = status === 'no_script';
    }
    if (this.hasUndoNoScriptButtonTarget) {
      this.undoNoScriptButtonTarget.hidden = status !== 'no_script';
    }
    this.updateFetchAction(status);
  }

  updateFetchAction(status) {
    if (!this.hasFetchButtonTarget) return;

    const canFetch = status === 'ready' || status === 'captured' || status === 'failed';
    this.fetchButtonTarget.hidden = !canFetch;
    this.fetchButtonTarget.textContent = status === 'captured' ? 'Re-fetch' : (status === 'failed' ? 'Retry' : 'Fetch');
    this.fetchButtonTarget.disabled = false;
  }

  async fetchScript(event) {
    event.preventDefault();
    if (this.noScriptLock || this.removing) return;

    const url = event.currentTarget.dataset.fetchUrl || this.fetchButtonTarget?.dataset?.fetchUrl;
    if (!url) return;

    this.clearTimer();
    if (this.inFlight) {
      try {
        await this.inFlight;
      } catch {
        // ignore
      }
    }

    this.showSaveStatus('saving', 'Fetching…');
    if (this.hasFetchButtonTarget) this.fetchButtonTarget.disabled = true;

    const body = new FormData();
    const token = this.csrfToken();
    if (token) body.append('authenticity_token', token);

    this.inFlight = fetch(url, {
      method: 'POST',
      body,
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': this.csrfToken(),
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    })
      .then(async (response) => {
        const contentType = response.headers.get('content-type') || '';
        const data = contentType.includes('application/json')
          ? await response.json().catch(() => ({}))
          : {};

        if (!response.ok || data.ok !== true) {
          const message = Array.isArray(data.errors) ? data.errors.join(', ') : data.error;
          if (data.apps_script_harvest_status) this.applyResponse(data);
          throw new Error(message || `Could not fetch (${response.status})`);
        }

        this.applyResponse(data);
        const count = data.files_count;
        this.showSaveStatus('saved', count != null ? `Fetched ${count}` : 'Fetched');
      })
      .catch((error) => {
        this.showSaveStatus('error', error.message);
        throw error;
      })
      .finally(() => {
        this.inFlight = null;
        if (this.hasFetchButtonTarget) this.fetchButtonTarget.disabled = false;
      });

    await this.inFlight;
  }

  async markNoScript(event) {
    event.preventDefault();
    this.clearTimer();
    this.noScriptLock = true;

    try {
      if (this.inFlight) {
        try {
          await this.inFlight;
        } catch {
          // ignore failed prior save; no_script should win
        }
      }

      this.showSaveStatus('saving');

      const body = this.buildPatchBody({
        apps_script_url: '',
        google_sheet_account: '',
        no_script: true
      });

      this.inFlight = this.patch(body)
        .then((data) => {
          this.applyResponse(data);
          this.showSaveStatus('saved');
        })
        .catch((error) => {
          this.showSaveStatus('error', error.message);
          throw error;
        })
        .finally(() => {
          this.inFlight = null;
        });

      await this.inFlight;
    } finally {
      this.noScriptLock = false;
    }
  }

  async undoNoScript(event) {
    event.preventDefault();
    this.clearTimer();
    this.noScriptLock = true;

    try {
      if (this.inFlight) {
        try {
          await this.inFlight;
        } catch {
          // ignore
        }
      }

      this.showSaveStatus('saving');

      const noScriptInput = this.noScriptField();
      if (noScriptInput) noScriptInput.value = '0';

      const body = this.buildPatchBody({
        apps_script_url: '',
        google_sheet_account: ''
      });

      this.inFlight = this.patch(body)
        .then((data) => {
          this.applyResponse(data);
          this.showSaveStatus('saved');
        })
        .catch((error) => {
          this.showSaveStatus('error', error.message);
          throw error;
        })
        .finally(() => {
          this.inFlight = null;
        });

      await this.inFlight;
    } finally {
      this.noScriptLock = false;
    }
  }

  async save() {
    if (this.noScriptLock) return;
    if (!this.isDirty()) return;

    this.normalizeUrlField();

    if (!this.isDirty()) return;

    if (this.inFlight) {
      await this.inFlight.catch(() => {});
      if (this.isDirty()) return this.save();
      return;
    }

    this.showSaveStatus('saving');

    const body = this.buildPatchBody();
    body.delete('video[no_script]');

    this.inFlight = this.patch(body)
      .then((data) => {
        this.applyResponse(data);
        this.showSaveStatus('saved');
      })
      .catch((error) => {
        this.showSaveStatus('error', error.message);
        throw error;
      })
      .finally(() => {
        this.inFlight = null;
      });

    await this.inFlight;
  }

  patch(body) {
    return fetch(this.requestUrl(), {
      method: 'POST',
      body,
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': this.csrfToken(),
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    }).then(async (response) => {
      const contentType = response.headers.get('content-type') || '';
      const data = contentType.includes('application/json')
        ? await response.json().catch(() => ({}))
        : {};

      if (!response.ok) {
        const message = Array.isArray(data.errors) ? data.errors.join(', ') : data.error;
        throw new Error(message || `Could not save (${response.status})`);
      }

      if (data.ok !== true) {
        throw new Error('Could not save');
      }

      return data;
    });
  }

  saveKeepalive() {
    if (this.noScriptLock || this.removing) return;

    fetch(this.requestUrl(), {
      method: 'POST',
      body: this.buildPatchBody(),
      keepalive: true,
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': this.csrfToken(),
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    });
  }

  applyResponse(data) {
    const status = data.apps_script_harvest_status || 'pending';
    const label = data.apps_script_harvest_status_label || status.replaceAll('_', ' ');

    this.statusBadgeTarget.textContent = label;
    this.statusBadgeTarget.className = STATUS_BADGE_CLASSES[status] || STATUS_BADGE_CLASSES.pending;

    if (data.apps_script_id) {
      this.scriptIdTarget.textContent = data.apps_script_id;
      this.scriptIdTarget.title = data.apps_script_id;
      this.scriptIdTarget.hidden = false;
    } else {
      this.scriptIdTarget.textContent = '';
      this.scriptIdTarget.hidden = true;
    }

    const urlInput = this.field('video[apps_script_url]');

    if (urlInput && !data.apps_script_url) urlInput.value = '';
    if (status === 'no_script') {
      if (this.hasAccountInputTarget) this.accountInputTarget.value = '';
      const noScriptInput = this.noScriptField();
      if (noScriptInput) noScriptInput.value = '1';
    } else {
      const noScriptInput = this.noScriptField();
      if (noScriptInput) noScriptInput.value = '0';
    }

    this.updateAccountSelection();
    this.updateNoScriptActions(status);
    this.updateErrorMessage(data.apps_script_harvest_error, status);

    this.snapshot = this.snapshotFromServer(data);
    this.dispatch('updated', { detail: { stats: data.stats, status }, bubbles: true });
    this.maybeRemoveFromFilter(status);
  }

  snapshotFromServer(data) {
    const params = new URLSearchParams();
    params.set('video[apps_script_url]', data.apps_script_url ?? '');
    params.set('video[google_sheet_account]', data.google_sheet_account ?? '');
    return params.toString();
  }

  shouldDeferUrlOnlySave() {
    const page = this.element.closest('[data-controller~="apps-script-harvest"]');
    if (!page) return false;

    const filter = page.dataset.appsScriptHarvestFilterValue || 'all';
    if (filter !== 'needs_script') return false;

    return !this.fieldValue('video[google_sheet_account]').trim();
  }

  maybeRemoveFromFilter(status) {
    const page = this.element.closest('[data-controller~="apps-script-harvest"]');
    if (!page) return;

    const filter = page.dataset.appsScriptHarvestFilterValue || 'all';
    if (filter === 'all') return;
    if (this.matchesFilter(status, filter)) return;

    this.removeRow();
  }

  matchesFilter(status, filter) {
    switch (filter) {
      case 'needs_script':
        return status === 'pending';
      case 'ready':
        return status === 'ready';
      case 'done':
        return status === 'captured';
      case 'failed':
        return status === 'failed';
      case 'no_script':
        return status === 'no_script';
      default:
        return true;
    }
  }

  updateErrorMessage(message, status) {
    if (!this.hasErrorMessageTarget) return;

    const text = message?.toString().trim() || '';
    const show = status === 'failed' && text.length > 0;
    this.errorMessageTarget.textContent = text;
    this.errorMessageTarget.title = text;
    this.errorMessageTarget.hidden = !show;
  }

  removeRow() {
    this.removing = true;
    const row = this.element;
    row.classList.add('apps-script-harvest__row--leaving');

    window.setTimeout(() => {
      row.remove();
      this.dispatch('removed', { bubbles: true });
    }, ROW_REMOVE_MS);
  }

  isDirty() {
    return this.serialize() !== this.snapshot;
  }

  serialize() {
    const params = new URLSearchParams();
    params.set('video[apps_script_url]', this.fieldValue('video[apps_script_url]'));
    params.set('video[google_sheet_account]', this.fieldValue('video[google_sheet_account]'));
    return params.toString();
  }

  buildPatchBody(overrides = {}) {
    const body = new FormData();

    const token = this.csrfToken();
    if (token) body.append('authenticity_token', token);

    body.append('_method', 'patch');
    body.append(
      'video[apps_script_url]',
      overrides.apps_script_url ?? this.fieldValue('video[apps_script_url]')
    );
    body.append(
      'video[google_sheet_account]',
      overrides.google_sheet_account ?? this.fieldValue('video[google_sheet_account]')
    );

    if (overrides.no_script) body.append('video[no_script]', 'true');

    return body;
  }

  requestUrl() {
    return this.formTarget?.action || this.element.dataset.updateUrl;
  }

  fieldValue(name) {
    return this.field(name)?.value ?? '';
  }

  showSaveStatus(state, message = '') {
    if (!this.hasSaveStatusTarget) return;

    const el = this.saveStatusTarget;
    this.clearFadeTimer();

    const labels = {
      saving: message || 'Saving…',
      saved: message || 'Saved',
      error: message || 'Could not save'
    };

    el.textContent = labels[state] || '';
    el.hidden = false;
    el.dataset.state = state;

    if (state === 'saved') {
      this.fadeTimer = window.setTimeout(() => {
        el.hidden = true;
        el.textContent = '';
        delete el.dataset.state;
      }, SAVED_HIDE_MS);
    }
  }

  clearTimer() {
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  clearFadeTimer() {
    if (this.fadeTimer) {
      window.clearTimeout(this.fadeTimer);
      this.fadeTimer = null;
    }
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }

  normalizeUrlField() {
    const urlInput = this.field('video[apps_script_url]');
    if (!urlInput) return;

    const value = urlInput.value.trim();
    if (!value) return;
    if (this.looksLikeScriptUrl(value)) return;

    urlInput.value = '';
  }

  looksLikeScriptUrl(value) {
    return /script\.google\.com/i.test(value);
  }

  field(name) {
    return this.element.querySelector(`[name="${name}"]`);
  }

  noScriptField() {
    return this.element.querySelector(`#${this.formTarget.id}_no_script`);
  }
}
